import type { DeskDetail } from '@deskworks/shared-types';
import { BookOpen, Pencil } from 'lucide-react';
import { useState } from 'react';
import { Markdown } from '@/components/markdown';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { EmptyState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

export function DeskPlaybookSection({ desk }: { desk: DeskDetail }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const playbook = desk.guidelines ?? desk.playbook;

  const save = useApiMutation(() => api.desks.putGuidelines(desk.id, draft), {
    success: t('Guidelines saved'),
    invalidate: [qk.desk(desk.id)],
    onSuccess: () => setEditing(false),
  });

  const startEdit = () => {
    setDraft(playbook?.content ?? '');
    setEditing(true);
  };

  return (
    <Section
      icon={<BookOpen />}
      title={t('Guidelines')}
      description={t('Written by you: what this desk may do and how it presents itself. Binding for the Lead and every agent.')}
      actions={
        !editing && (
          <Button variant="outline" size="sm" onClick={startEdit}>
            <Pencil /> {playbook ? t('Edit') : t('Write guidelines')}
          </Button>
        )
      }
    >
      {editing ? (
        <div className="grid gap-3">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={14}
            className="font-mono text-[13px] leading-relaxed"
            autoFocus
            placeholder={t('e.g.\n- Always reply in English and in a friendly tone.\n- No refunds over €100 without approval.')}
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">{t('Saving creates a new version. Work in progress uses the new version from its next start.')}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                {t('Cancel')}
              </Button>
              <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
                {t('Save')}
              </Button>
            </div>
          </div>
        </div>
      ) : playbook && playbook.content.trim() ? (
        <>
          <div className="max-h-[28rem] overflow-y-auto rounded-lg border bg-muted/30 p-4">
            <Markdown text={playbook.content} />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {tr('Version {n} · last changed {time}', { n: playbook.version, time: <RelativeTime value={playbook.updatedAt} /> })}
          </p>
        </>
      ) : (
        <EmptyState
          compact
          title={t('No guidelines yet')}
          description={t('Write down what this desk may do and how: tone, limits, when it needs to ask you. The Lead writes its own playbooks for how its team works.')}
        />
      )}
    </Section>
  );
}
